import React, { useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '@mui/material/styles'
import Paper from '@mui/material/Paper'
import Button from '@/v2/components/common/Button'
import Typography from '@/v2/components/common/Typography'
import { usePatientContext } from '@/v2/contexts/PatientContext'
import { useNavigate } from 'react-router-dom'
import { useSendInsuranceLink } from '@/v2/hooks/insurance/useInsuranceForm'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import Accordion from '@/v2/components/common/Accordion'

const Body = styled.div``
const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
`

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`

export const AccordionStyled = styled(Accordion)`
  width: 98%;
`

export const InfoBox = styled.div`
  background-color: #f9f9f9; /* light gray background */
  border-left: 4px solid #ef0606; /* green line on the left */
  padding: 10px;
  margin-top: 1rem;
  margin-bottom: 1rem;
  font-size: 1rem; /* adjust if needed */
  line-height: 1.5;
  border-radius: 4px;
`

const PatientInsurance = () => {
  const theme = useTheme()
  const { patient } = usePatientContext()
  const insuranceForms = patient?.insuranceForms || []
  const navigate = useNavigate()
  const { sendInsuranceLink } = useSendInsuranceLink(patient.id)
  const { onOpenSnackbar } = useSnackbarContext()
  const [disabled, setDisabled] = useState(false)

  const copyToClipboard = text => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {})
        .catch(err => {
          console.error('Failed to copy text: ', err)
        })
    } else {
      // Fallback method for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        document.execCommand('copy')
      } catch (err) {
        console.error('Failed to copy text: ', err)
      }
      document.body.removeChild(textArea)
    }
  }

  const disableDebounce = (fn, time) => {
    setTimeout(() => {
      fn()
    }, time)
  }

  const handleInsuranceLink = async copyOnly => {
    if (!copyOnly) {
      const res = await sendInsuranceLink()
      if (!res.error) {
        setDisabled(true)

        disableDebounce(() => {
          setDisabled(false)
        }, 5000)

        onOpenSnackbar(res.message)
      }
      return
    }

    const link = `${window.location.origin}/v2/insurance-form/${patient.hashid}`
    copyToClipboard(link)
    onOpenSnackbar('URL copied to clipboard.')
  }

  const handleAddInsuranceManually = () => {
    navigate(`/v2/insurance-form/${patient.hashid}`, {
      state: { patient },
    })
  }

  return (
    <Paper
      sx={{ padding: '30px', marginBottom: '20px' }}
      id="patient-insurance"
    >
      <Line>
        <Typography variant="h3" sx={{ mb: '28px' }}>
          Insurance Information
        </Typography>
        <Button
          variant="text"
          onClick={() => handleInsuranceLink()}
          sx={{ color: theme.palette.gray.dark }}
          disabled={disabled}
        >
          <Typography variant="h5">Send Insurance Link</Typography>
        </Button>
      </Line>

      <Line mt="10px" mb="10px">
        <Typography variant="h6" sx={{ color: theme.palette.gray.dark }}>
          {insuranceForms.length > 0
            ? ' '
            : 'No insurance information provided.'}
        </Typography>
        <Button
          variant="text"
          onClick={() => handleInsuranceLink(true)}
          sx={{ color: theme.palette.gray.dark }}
        >
          <Typography variant="h5">Copy Insurance Link</Typography>
        </Button>
      </Line>

      <Line mt="10px" mb="10px">
        <Typography
          variant="h6"
          sx={{ color: theme.palette.gray.dark }}
        ></Typography>
        <Button
          variant="text"
          onClick={() => handleAddInsuranceManually()}
          sx={{ color: theme.palette.gray.dark }}
        >
          <Typography variant="h5">Add Insurance Manually</Typography>
        </Button>
      </Line>

      {insuranceForms.length > 0 && (
        <Body>
          {insuranceForms?.map((insuranceForm, index) => (
            <AccordionStyled
              noPaddingTitle
              noBackground
              key={index}
              defaultExpanded={insuranceForm.status === 'draft'}
              title={
                <Typography>
                  {insuranceForm?.status === 'draft' ? (
                    <Typography
                      variant="h4"
                      sx={{
                        color: theme.palette.gray.dark,
                        textTransform: 'uppercase',
                        marginTop: '5px',
                      }}
                    >
                      {insuranceForm.status} insurance
                    </Typography>
                  ) : (
                    <Typography>
                      {' '}
                      {insuranceForm?.patient?.insuranceCompany
                        ? ` ${insuranceForm?.patient?.insuranceCompany} – Member #
                      ${insuranceForm?.patient?.memberId}`
                        : 'Insurance Information Not Provided'}
                    </Typography>
                  )}
                </Typography>
              }
              content={
                <>
                  {insuranceForm?.status === 'draft' && (
                    <Line mt="10px">
                      <InfoBox>
                        Patient has provided insurance information, however, to
                        use it, a staff member must review and complete the form
                        if needed.
                      </InfoBox>
                    </Line>
                  )}
                  <Line mt="10px">
                    <Typography
                      variant="h6"
                      sx={{ color: theme.palette.gray.dark }}
                    >
                      {' '}
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() =>
                        navigate(`/v2/insurance-form/${patient.hashid}`, {
                          state: { insuranceForm, patient },
                        })
                      }
                      sx={{ color: theme.palette.gray.dark }}
                    >
                      <Typography variant="h5">
                        {insuranceForm?.status === 'draft' ? 'Review' : 'Edit'}
                      </Typography>
                    </Button>
                  </Line>
                  <Column>
                    <Typography
                      variant="h6"
                      sx={{ color: theme.palette.gray.dark }}
                    >
                      Patient Name
                    </Typography>
                    <Typography
                      bold
                      fontSize="14px"
                      sx={{
                        mb: '16px',
                        mt: '2px',
                        color: theme.palette.black.main,
                      }}
                    >
                      {insuranceForm?.patient?.firstName || 'Not informed'}{' '}
                      {insuranceForm?.patient?.lastName}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: theme.palette.gray.dark }}
                    >
                      Insurance Company
                    </Typography>
                    <Typography
                      bold
                      fontSize="14px"
                      sx={{
                        mb: '16px',
                        mt: '2px',
                        color: theme.palette.black.main,
                      }}
                    >
                      {insuranceForm?.patient?.insuranceCompany ||
                        'Not informed'}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: theme.palette.gray.dark }}
                    >
                      Plan Group Number
                    </Typography>
                    <Typography
                      bold
                      fontSize="14px"
                      sx={{
                        mb: '16px',
                        mt: '2px',
                        color: theme.palette.black.main,
                      }}
                    >
                      {insuranceForm?.patient?.planGroupNumber ||
                        'Not informed'}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ color: theme.palette.gray.dark }}
                    >
                      Policy Holder Subscriber ID
                    </Typography>
                    <Typography
                      bold
                      fontSize="14px"
                      sx={{
                        mb: '16px',
                        mt: '2px',
                        color: theme.palette.black.main,
                      }}
                    >
                      {insuranceForm?.patient
                        ?.staffConsultPolicyHolderSubscriberId ||
                        'Not informed'}
                    </Typography>
                  </Column>
                  {insuranceForm?.subscriber?.firstName && (
                    <Column>
                      <Typography
                        variant="h6"
                        sx={{ color: theme.palette.gray.dark }}
                      >
                        Subscriber Name
                      </Typography>
                      <Typography
                        bold
                        fontSize="14px"
                        sx={{
                          mb: '16px',
                          mt: '2px',
                          color: theme.palette.black.main,
                        }}
                      >
                        {insuranceForm?.subscriber?.firstName || 'Not informed'}{' '}
                        {insuranceForm?.subscriber?.lastName}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: theme.palette.gray.dark }}
                      >
                        Relationship to Patient
                      </Typography>
                      <Typography
                        bold
                        fontSize="14px"
                        sx={{
                          mb: '16px',
                          mt: '2px',
                          color: theme.palette.black.main,
                        }}
                      >
                        {insuranceForm?.subscriber?.relationToPatient
                          ? insuranceForm.subscriber.relationToPatient
                              .charAt(0)
                              .toUpperCase() +
                            insuranceForm.subscriber.relationToPatient.slice(1)
                          : 'Not informed'}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ color: theme.palette.gray.dark }}
                      >
                        Subscriber Member ID
                      </Typography>
                      <Typography
                        bold
                        fontSize="14px"
                        sx={{
                          mb: '16px',
                          mt: '2px',
                          color: theme.palette.black.main,
                        }}
                      >
                        {insuranceForm?.subscriber?.memberId || 'Not informed'}
                      </Typography>
                    </Column>
                  )}
                </>
              }
            />
          ))}
        </Body>
      )}
    </Paper>
  )
}

export default PatientInsurance
